import {
  AttachmentIcon,
  Button,
  DeleteIcon,
  Link,
  Text
} from '@lmc/design-system'
import { cssVars } from '@lmc/design-system/tokens'
import type { IncidentReport } from '@lmc/ts-common'
import * as Sentry from '@sentry/react'
import { isApiError } from 'common/services/apiError'
import { showToast } from 'common/utils'
import useIncidentReport from 'hooks/useIncidentReport/useIncidentReport'
import useIncidentReportByLog from 'hooks/useIncidentReport/useIncidentReportByLog'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { type ChangeEvent, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { StepSection } from '../../stepLayout'

type AttachmentChanges = {
  /** Picked on this visit; uploaded once the step is saved. */
  queued: ReadonlyArray<File>
  /** Saved attachments, by `fileKey`, deleted once the step is saved. */
  removed: ReadonlyArray<string>
}

const NO_CHANGES: AttachmentChanges = { queued: [], removed: [] }

/**
 * A saved attachment's S3 object, which outlives its URL: every read signs the
 * URLs afresh, changing only the query.
 */
export const fileKey = (url: string) => url.split('?')[0]

const hasAnyChanges = ({ queued, removed }: AttachmentChanges) =>
  queued.length > 0 || removed.length > 0

/**
 * The case's attachments on step 1. Files picked and removed are held until
 * the step is saved, since a new case has nowhere to attach them before then;
 * `save` then applies them through the endpoints the v1 form uses.
 */
export const useCaseAttachments = ({
  incidentReport,
  onChange
}: {
  incidentReport: IncidentReport
  /** Whether anything is waiting to be saved; drives the leave prompt. */
  onChange: (hasChanges: boolean) => void
}) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const { deleteIncidentReportFiles, uploadIncidentReportFiles } =
    useIncidentReport({
      incidentReportId: incidentReport.id,
      incidentLogId: incidentReport.log.id,
      // The list showing the files is the confirmation; failure has its own.
      notifyFileChanges: false
    })
  // Reads the report on demand only; the step's own copy may be stale.
  const { refetch: readReport } = useIncidentReportByLog({
    incidentLogId: incidentReport.log.id,
    enabled: false
  })
  const [changes, setChangesState] = useState(NO_CHANGES)
  // The step's handle reads this as soon as a save settles, before a render.
  const changesRef = useRef(NO_CHANGES)
  const [isSaving, setIsSaving] = useState(false)
  // Deleted, but still listed until the report is read again.
  const [deleted, setDeleted] = useState<ReadonlyArray<string>>([])

  const setChanges = (
    update: (current: AttachmentChanges) => AttachmentChanges
  ) => {
    const next = update(changesRef.current)
    changesRef.current = next
    setChangesState(next)
    onChange(hasAnyChanges(next))
  }

  /** Resolves false, having said why, when the step should stay open. */
  const save = async () => {
    const { queued, removed } = changesRef.current
    if (!hasAnyChanges(changesRef.current)) {
      return true
    }
    setIsSaving(true)
    try {
      // First, as on the v1 form. The backend deletes by position, so the
      // positions come from a fresh read, not from the list on screen.
      if (removed.length > 0) {
        const { data } = await readReport({ throwOnError: true })
        const deletedIndexes = (data?.data.attachments ?? []).flatMap(
          (url, index) => (removed.includes(fileKey(url)) ? [index] : [])
        )
        if (deletedIndexes.length > 0) {
          await deleteIncidentReportFiles({ deletedIndexes })
        }
        setDeleted((current) => [...current, ...removed])
        setChanges((current) => ({
          ...current,
          removed: current.removed.filter((key) => !removed.includes(key))
        }))
      }
      if (queued.length > 0) {
        // One request: the endpoint takes every file under `attachments`.
        const formData = new FormData()
        queued.forEach((file) => {
          formData.append('attachments', file, file.name)
        })
        await uploadIncidentReportFiles(formData)
        setChanges((current) => ({
          ...current,
          queued: current.queued.filter((file) => !queued.includes(file))
        }))
      }
      return true
    } catch (error) {
      if (!isApiError(error)) {
        Sentry.captureException(error)
      }
      showToast({
        type: 'error',
        title: t(incidentReportsConstants.CASE_V2_INFO_ATTACHMENTS_SAVE_FAILED)
      })
      return false
    } finally {
      setIsSaving(false)
    }
  }

  return {
    changes,
    /** Whether a saved attachment is gone, or going once the step is saved. */
    isRemoved: (url: string) =>
      changes.removed.includes(fileKey(url)) || deleted.includes(fileKey(url)),
    hasChanges: () => hasAnyChanges(changesRef.current),
    isSaving,
    queue: (files: ReadonlyArray<File>) =>
      setChanges((current) => ({
        ...current,
        queued: [...current.queued, ...files]
      })),
    remove: (url: string) =>
      setChanges((current) => ({
        ...current,
        removed: [...current.removed, fileKey(url)]
      })),
    save,
    unqueue: (file: File) =>
      setChanges((current) => ({
        ...current,
        queued: current.queued.filter((queued) => queued !== file)
      }))
  }
}

type CaseAttachmentsProps = {
  /** The report's saved attachments, as URLs. */
  attachments: ReadonlyArray<string>
  field: ReturnType<typeof useCaseAttachments>
  testId: string
}

/** The saved attachments as links, the files waiting to upload, and Add. */
const CaseAttachments = ({
  attachments,
  field,
  testId
}: CaseAttachmentsProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const inputRef = useRef<HTMLInputElement>(null)
  const { changes, isSaving } = field
  const queuedCount = changes.queued.length

  const pick = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? [])
    // Cleared so picking the same file again still fires a change.
    event.target.value = ''
    if (files.length > 0) {
      field.queue(files)
    }
  }

  return (
    <StepSection title={t(incidentReportsConstants.CASE_V2_INFO_ATTACHMENTS)}>
      <Field data-testid={testId}>
        <Rows>
          {attachments.map((url, index) => {
            if (field.isRemoved(url)) {
              return null
            }
            const name = t(
              incidentReportsConstants.CASE_V2_INFO_ATTACHMENTS_FILE,
              { number: index + 1 }
            )
            return (
              <Row key={fileKey(url)} data-testid={`${testId}-saved-${index}`}>
                <Link
                  href={url}
                  newTabLabel={t(
                    incidentReportsConstants.CASE_V2_INVESTIGATION_OPENS_IN_NEW_TAB
                  )}
                >
                  {name}
                </Link>
                <Button
                  aria-label={t(
                    incidentReportsConstants.CASE_V2_INFO_ATTACHMENTS_REMOVE,
                    { name }
                  )}
                  data-testid={`${testId}-saved-${index}-remove`}
                  disabled={isSaving}
                  icon={<DeleteIcon size={16} />}
                  onClick={() => field.remove(url)}
                  variant="transparent-critical"
                >
                  {t(incidentReportsConstants.CASE_V2_REMOVE)}
                </Button>
              </Row>
            )
          })}
          {changes.queued.map((file, index) => (
            <Row
              // biome-ignore lint/suspicious/noArrayIndexKey: two picks can share a name
              key={`${file.name}-${index}`}
              data-testid={`${testId}-queued-${index}`}
            >
              <Text>{file.name}</Text>
              <Button
                aria-label={t(
                  incidentReportsConstants.CASE_V2_INFO_ATTACHMENTS_REMOVE,
                  { name: file.name }
                )}
                data-testid={`${testId}-queued-${index}-remove`}
                disabled={isSaving}
                icon={<DeleteIcon size={16} />}
                onClick={() => field.unqueue(file)}
                variant="transparent-critical"
              >
                {t(incidentReportsConstants.CASE_V2_REMOVE)}
              </Button>
            </Row>
          ))}
        </Rows>
        {queuedCount > 0 && (
          <Text tone="secondary" role="status" data-testid={`${testId}-status`}>
            {t(
              isSaving
                ? incidentReportsConstants.CASE_V2_INFO_ATTACHMENTS_UPLOADING
                : incidentReportsConstants.CASE_V2_INFO_ATTACHMENTS_QUEUED,
              { count: queuedCount }
            )}
          </Text>
        )}
        <Add>
          <Button
            data-testid={`${testId}-add`}
            disabled={isSaving}
            icon={<AttachmentIcon size={16} />}
            onClick={() => inputRef.current?.click()}
            variant="transparent"
          >
            {t(incidentReportsConstants.CASE_V2_INFO_ATTACHMENTS_ADD)}
          </Button>
          <input
            ref={inputRef}
            type="file"
            multiple
            hidden
            onChange={pick}
            data-testid={`${testId}-input`}
          />
        </Add>
      </Field>
    </StepSection>
  )
}

const { spacing } = cssVars

const Add = styled.div`
  display: flex;
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[150]};
`

const Row = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[50]} ${spacing[200]};
  justify-content: space-between;
`

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[100]};

  &:empty {
    display: none;
  }
`

export default CaseAttachments
